import { faChevronDown, faChevronRight, faCube, faFolder, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setInvoicePositionQuantity } from "../../../../store/slices/invoiceV2Slice";
import { formatCurrencyToLocale } from "../../../../utils/method";
import ExpandablePositionsTable from "./ExpandablePositionsTable";
import './RecursiveGroupRenderer.scss';

// Recursive Group Renderer Component
const RecursiveGroupRenderer = ({
    element,
    level = 0,
    expandedGroups,
    toggleGroup,
    isReadonly = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Handle position quantity changes directly with Redux
    const handlePositionQuantityChange = (position, newQuantity) => {
        dispatch(setInvoicePositionQuantity({
            positionId: position.id,
            quantity: parseFloat(newQuantity) || 0,
            position: position // Pass complete position object for LV data
        }));
    };

    // If this element is not a group, it's a position - don't render it here
    if (!element.isGroup) {
        return null;
    }

    const isExpanded = expandedGroups.has(element.id);
    const positions = element.elements ? element.elements.filter(el => !el.isGroup) : [];
    const subGroups = element.elements ? element.elements.filter(el => el.isGroup) : [];

    return (
        <div className={`rgr-container rgr-container--level-${Math.min(level, 4)}`}>
            {/* Group Header */}
            <div
                className={`cursor-pointer d-flex justify-content-between align-items-center rgr-header rgr-header--border-${level % 5}`}
                onClick={() => toggleGroup(element.id)}
            >
                <div className="d-flex align-items-center">
                    <div className="me-2 d-flex align-items-center justify-content-center rgr-chevron-box">
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            className="group-icon-sm"
                        />
                    </div>
                    <FontAwesomeIcon
                        icon={isExpanded ? faFolderOpen : faFolder}
                        className="me-2 group-icon-md"
                    />
                    <h6 className="mb-0 fw-semibold rgr-title">
                        {element.name}
                    </h6>
                </div>
                <div className="d-flex align-items-center">
                    {positions.length > 0 && (
                        <Badge className="me-2 px-2 py-1 rgr-badge--positions">
                            <FontAwesomeIcon icon={faCube} className="me-1" size="xs" />
                            {positions.length} {t('hierarchical-positions.positions')}
                        </Badge>
                    )}
                    {subGroups.length > 0 && (
                        <Badge className="me-2 px-2 py-1 rgr-badge--groups">
                            <FontAwesomeIcon icon={faFolder} className="me-1" size="xs" />
                            {subGroups.length} {t('hierarchical-positions.groups')}
                        </Badge>
                    )}
                    <div className="fw-bold rgr-total">
                        {formatCurrencyToLocale(element.totalAmount)}
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && element.elements && (
                <div className="rgr-body">
                    {/* Render all sub-groups recursively */}
                    {subGroups.map(subGroup => (
                        <RecursiveGroupRenderer
                            key={subGroup.id}
                            element={subGroup}
                            level={level + 1}
                            expandedGroups={expandedGroups}
                            toggleGroup={toggleGroup}
                            onPositionQuantityChange={handlePositionQuantityChange}
                            isReadonly={isReadonly}
                        />
                    ))}

                    {/* Render positions table if there are any positions at this level */}
                    {positions.length > 0 && (
                        <div className={`rgr-positions-table${subGroups.length > 0 ? ' rgr-positions-table--has-siblings' : ''}`}>
                            <ExpandablePositionsTable
                                positions={positions}
                                onInvoiceQuantityChange={handlePositionQuantityChange}
                                isReadonly={isReadonly}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RecursiveGroupRenderer;
